/**
 * useOpeningsByRoomId — derives a `roomId → openingIds[]` reverse-index from
 * the annotation store + each opening's `opening_room_ids` reactive property.
 */

import { useSyncExternalStore } from 'react';
import { getOpeningRoomIdsProperty } from '@pointorama/potree-shared';
import { annotationStore } from './useAnnotationStore';

export interface OpeningsIndex {
  byRoomId: Record<string, string[]>;
  allOpeningIds: Set<string>;
}

const EMPTY: OpeningsIndex = { byRoomId: {}, allOpeningIds: new Set() };

const sortedOpeningIds = (): string[] => {
  const byId = annotationStore.getState().byIdentifier;
  const ids: string[] = [];
  for (const key in byId) {
    if (byId[key].type === 'OPENING') ids.push(key);
  }
  ids.sort();
  return ids;
};

const sameIds = (a: string[], b: string[]): boolean => a.length === b.length && a.every((x, i) => x === b[i]);

/**
 * Builds the index from the current store state, reusing `prev`'s objects wherever the
 * contents are unchanged. The identity stability is load-bearing: `byRoomId[roomId]` is
 * passed as `linkedOpeningIds` into memoized Annotation rows, and both `byRoomId` and
 * `allOpeningIds` sit in downstream useMemo/useCallback dependency arrays.
 */
const buildIndex = (prev: OpeningsIndex): OpeningsIndex => {
  const openingIds = sortedOpeningIds();

  const byRoomId: Record<string, string[]> = {};
  for (const openingId of openingIds) {
    const roomIds = getOpeningRoomIdsProperty(openingId).value;
    for (const roomId of roomIds) {
      (byRoomId[roomId] ||= []).push(openingId);
    }
  }
  const allOpeningIds = new Set(openingIds);

  // Shape compare for a stable top-level reference.
  let same = prev.allOpeningIds.size === allOpeningIds.size;
  if (same) {
    for (const id of allOpeningIds) {
      if (!prev.allOpeningIds.has(id)) {
        same = false;
        break;
      }
    }
  }
  if (same) {
    const prevKeys = Object.keys(prev.byRoomId);
    const nextKeys = Object.keys(byRoomId);
    if (prevKeys.length !== nextKeys.length) {
      same = false;
    } else {
      for (const k of nextKeys) {
        const a = prev.byRoomId[k];
        if (!a || !sameIds(a, byRoomId[k])) {
          same = false;
          break;
        }
      }
    }
  }
  if (same) return prev;

  // Reuse per-room arrays when contents are unchanged so memoized child rows keep a
  // stable `linkedOpeningIds` reference.
  const stableByRoomId: Record<string, string[]> = {};
  for (const k in byRoomId) {
    const a = prev.byRoomId[k];
    stableByRoomId[k] = a && sameIds(a, byRoomId[k]) ? a : byRoomId[k];
  }

  return { byRoomId: stableByRoomId, allOpeningIds };
};

// ---------------------------------------------------------------------------
// Module-level derived store. The hook is called once per annotation row, so the cached
// index and the subscriptions are shared by every consumer rather than held per hook
// instance: one build per change, one subscription per opening, and an O(1) snapshot read
// when nothing changed (useSyncExternalStore calls getSnapshot on every render).
// ---------------------------------------------------------------------------

let cached: OpeningsIndex = EMPTY;
let dirty = true;
const listeners = new Set<() => void>();
let subscribedIds: string[] = [];
let openingSubs: { unsubscribe: () => void }[] = [];
let storeUnsub: (() => void) | null = null;

const invalidate = () => {
  dirty = true;
  for (const listener of listeners) listener();
};

/** Aligns the per-opening subscriptions with the store; returns true when the opening set changed. */
const syncOpeningSubscriptions = (): boolean => {
  const ids = sortedOpeningIds();
  if (sameIds(ids, subscribedIds)) return false;
  for (const sub of openingSubs) sub.unsubscribe();
  openingSubs = ids.map((id) => getOpeningRoomIdsProperty(id).subscribeToChanges(invalidate));
  subscribedIds = ids;
  return true;
};

const subscribe = (onStoreChange: () => void) => {
  listeners.add(onStoreChange);
  if (listeners.size === 1) {
    // The stores may have moved between the first consumer's render and this subscription.
    dirty = true;
    syncOpeningSubscriptions();
    // Unrelated annotation changes (rename, colour, ...) also notify the store; only a change
    // to the set of openings affects this index. Room-link changes arrive via the property
    // subscriptions above.
    storeUnsub = annotationStore.subscribe(() => {
      if (syncOpeningSubscriptions()) invalidate();
    });
  }
  return () => {
    listeners.delete(onStoreChange);
    if (listeners.size > 0) return;
    storeUnsub?.();
    storeUnsub = null;
    for (const sub of openingSubs) sub.unsubscribe();
    openingSubs = [];
    subscribedIds = [];
    dirty = true;
  };
};

const getSnapshot = (): OpeningsIndex => {
  if (dirty) {
    cached = buildIndex(cached);
    dirty = false;
  }
  return cached;
};

const getServerSnapshot = (): OpeningsIndex => EMPTY;

export function useOpeningsByRoomId(): OpeningsIndex {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}
